import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CharacterLimitNote } from "@/components/CharacterLimitNote";
import { RichTextEditor } from "@/components/RichTextEditor";

interface TextSettingSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** Title displayed in the sheet header */
  title: string;
  /** Optional description displayed below the title */
  description?: string;
  /** Current text value used to initialize the editor */
  value: string;
  /** Callback invoked with the updated text when the user clicks Apply */
  onApply: (value: string) => void;
  /** Placeholder text for the editor */
  placeholder?: string;
  /** Optional content shown under the title/description */
  headerContent?: ReactNode;
  /** Character limit to count the draft against; the draft can still go over it */
  characterLimit?: number | null;
}

/**
 * A generic sheet for editing text-based settings.
 * Opens with the current value and applies changes on confirmation.
 * @param props - TextSettingSheetProps
 * @returns Sheet element for text editing
 */
export function TextSettingSheet({
  open,
  onOpenChange,
  title,
  description,
  value,
  onApply,
  placeholder,
  headerContent,
  characterLimit = null,
}: TextSettingSheetProps) {
  const [localValue, setLocalValue] = useState("");

  useEffect(() => {
    if (open) {
      setLocalValue(value);
    }
  }, [open, value]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col overflow-hidden sm:w-[600px] sm:max-w-[600px]">
        <SheetHeader className="shrink-0">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
          {headerContent && <div className="pt-1">{headerContent}</div>}
        </SheetHeader>
        <div className="mt-4 flex min-h-0 flex-1 flex-col">
          <RichTextEditor
            value={localValue}
            onChange={setLocalValue}
            placeholder={placeholder}
            className="flex-1"
          />
          <CharacterLimitNote
            length={localValue.length}
            maxLength={characterLimit}
          />
          <div className="mt-4 flex shrink-0 justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                onApply(localValue);
                onOpenChange(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
